import * as yup from "yup";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { FaCircleNotch } from "react-icons/fa";
import { IoSaveOutline } from "react-icons/io5";
import { useState, useEffect } from "react";

import {
  MULTIMEDIA_PLACEHOLDER,
  MULTIMEDIA_MIME_TYPES,
  FILE_ALREADY_EXISTS,
} from "../../../utils/constants";

import Datepicker from "../../../components/common/Datepicker";
import FileUpload from "../../../components/common/FileUpload";
import Textbox from "../../../components/common/Textbox";
import Toaster from "../../../components/common/Toaster";

import complianceService from "../../../services/complianceService";
import moment from "moment";

const PTRForm = (props) => {
  const { data, account, isLoading, setLoading, setShowForm, onSuccess } =
    props;

  const [fileExists, setFileExists] = useState(false);
  const [registrationDate, setRegistrationDate] = useState(null);
  const [deadline, setDeadline] = useState(null);
  const [year, setYear] = useState(null);

  const [dateToday] = useState(
    Date.parse(moment(new Date(), "MM-DD-yyyy").toISOString())
  );

  const [currentYear] = useState(
    Date.parse(moment(new Date(), "YYYY").toISOString())
  );

  const schema = yup.object({
    id: yup.string(),
    number: yup.string().required("Please enter PTR No."),
    municipality: yup.string().required("Please enter municipality."),
    year: yup.string().required("Please select year"),
    deadline: yup.string().required("Please select deadline."),
    registration_date: yup
      .string()
      .required("Please select registration date."),
    files: yup
      .mixed()
      .test(
        "is-valid-file-name",
        "A string in a file name must match with the first name.",
        (value) => isValidFileName(value)
      ),
  });

  const {
    register,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema) });

  const isValidFileName = (file) => {
    if (file === undefined) return true;

    let first_name = account.first_name;
    let file_name = file[0].name;

    first_name = first_name.toLowerCase();
    file_name = file_name.toLowerCase();

    return file_name.includes(first_name);
  };

  const onSubmit = async (info) => {
    try {
      setLoading(true);

      const ptr = { ...info };
      ptr["account_id"] = account.id;
      ptr["year"] = info.year.slice(info.year.length - 4);

      const result = await complianceService.savePTRAsync(ptr);

      const item = { ...ptr };
      item["id"] = result.id;
      item["deadline"] = moment(deadline).format("LL");
      item["registration_date"] = moment(info.registration_date).format("LL");

      if (result.s3_reference_names && result.s3_reference_names.length > 0) {
        item["s3_reference_name"] = result.s3_reference_names[0];
        item["document_name"] = result.files[0].file_name;
      }

      Toaster.Success("PTR Saved Successfully");
      onSuccess(item);
      setShowForm(false);

      setLoading(false);
    } catch (ex) {
      if (ex.response && ex.response.data.message === FILE_ALREADY_EXISTS) {
        setFileExists(true);
        setLoading(false);
        return;
      }

      Toaster.Error("Something went wrong.");
      setLoading(false);
    }
  };

  const handleDateChange = (item) => {
    setRegistrationDate(item);

    const date = moment(item);
    const year = date.year();
    const month = date.month();
    const day = date.date();

    const next_year = moment([year, month, day]).add(1, "year");
    const formatted_date = next_year.format("MM-DD-yyyy");

    setDeadline(formatted_date);
    setValue("deadline", formatted_date);
    clearErrors("deadline");
  };

  useEffect(() => {
    if (data) {
      const registration = moment(data.registration_date).format("MM-DD-yyyy");
      const deadline = moment(data.deadline).format("MM-DD-yyyy");
      const year = moment(data.year).format("yyyy");

      setValue("id", data.id);
      setValue("number", data.number);
      setValue("municipality", data.municipality);
      setValue("registration_date", registration);
      setValue("deadline", deadline);
      setValue("year", year);

      setRegistrationDate(registration);
      setDeadline(deadline);
      setYear(year);
    }
  }, [data, setValue]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="p-8 space-y-6">
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <Textbox
            id="number-textbox"
            name="number"
            type="text"
            title="Number"
            register={register("number")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2">
          <Textbox
            id="municipality-textbox"
            name="municipality"
            type="text"
            title="City / Municipality"
            placeholder="e.g. Manila"
            register={register("municipality")}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2 md:col-span-1">
          <Datepicker
            id="registration-date-datepicker"
            name="registration_date"
            title="Date"
            dateFormat="MM-DD-yyyy"
            initialValue={dateToday}
            value={registrationDate}
            timeFormat={false}
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("registration_date")}
            onDateChange={(date) => handleDateChange(date)}
            errors={errors}
          />
        </div>
        <div className="col-span-2 md:col-span-1">
          <Datepicker
            id="deadline-date-datepicker"
            name="deadline"
            title="Deadline"
            dateFormat="MM-DD-yyyy"
            value={deadline}
            timeFormat={false}
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("deadline")}
            onDateChange={(date) => setDeadline(date)}
            errors={errors}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-6">
        <div className="col-span-2 md:col-span-1">
          <Datepicker
            id="year-datepicker"
            name="year"
            title="Year"
            dateFormat="YYYY"
            setValue={setValue}
            clearErrors={clearErrors}
            register={register("year")}
            timeFormat={false}
            initialValue={currentYear}
            onDateChange={(date) => setYear(date)}
            value={year}
            errors={errors}
            readOnly
          />
        </div>
      </div>
      {!data && (
        <div className="grid grid-cols-2">
          <div className="col-span-2">
            <FileUpload
              id="files-dropdown"
              name="files"
              title="Files"
              setValue={setValue}
              clearErrors={clearErrors}
              register={register("files")}
              errors={errors}
              accepts={MULTIMEDIA_MIME_TYPES}
              placeholder={MULTIMEDIA_PLACEHOLDER}
              maxFiles={1}
              maxSize={5242880}
              multiple={false}
            />
            {fileExists && (
              <div className="pt-2">
                <span className="text-sm text-red-400">
                  {FILE_ALREADY_EXISTS}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
      <div className="grid grid-cols-2 pt-2">
        <div className="col-span-2">
          <button
            type="submit"
            className="button button-primary disabled:opacity-70 disabled:cursor-not-allowed"
            disabled={isLoading}
          >
            {isLoading && <FaCircleNotch className="animate-spin mr-3" />}
            {!isLoading && <IoSaveOutline className="mr-3" />}
            Save Changes
          </button>
        </div>
      </div>
    </form>
  );
};

export default PTRForm;
